'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { ExternalLink, MapPin, Eye } from 'lucide-react';
import CommunityPostAvatar from '@/app/public-landing-page/components/CommunityPostAvatar';
import {
  LANDING_COMMUNITY_POSTS,
  type LandingCommunityPost,
} from '@/app/public-landing-page/components/landing-community-demo';
import { rainbowFrameStyle } from './ledger-rainbow';

function RainbowCard({
  children,
  className = '',
  active = false,
}: {
  children: React.ReactNode;
  className?: string;
  active?: boolean;
}) {
  return (
    <div
      className={`rounded-xl p-[2px] transition-shadow duration-200 ${active ? 'shadow-lg shadow-teal-500/20' : ''} ${className}`}
      style={rainbowFrameStyle()}
    >
      <div className="h-full rounded-[10px] bg-white">{children}</div>
    </div>
  );
}

export default function CommunityExplorerFeed() {
  const [selectedId, setSelectedId] = useState<string>(LANDING_COMMUNITY_POSTS[0].id);
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const selected =
    LANDING_COMMUNITY_POSTS.find((p) => p.id === selectedId) ?? LANDING_COMMUNITY_POSTS[0];
  const focusId = hoveredId ?? selectedId;
  const focusPost = LANDING_COMMUNITY_POSTS.find((p) => p.id === focusId) ?? selected;

  return (
    <aside className="min-w-0 2xl:border-l 2xl:border-teal-200/60 2xl:pl-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="text-xl font-black text-slate-950 sm:text-2xl">Community Explorer</h3>
          <p className="mt-1 text-sm text-slate-500">
            Example public record previews — each card represents a distinct member or institution.
          </p>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full border border-teal-200 bg-teal-50 px-3 py-1 text-[11px] font-bold text-teal-800">
          <Eye size={12} />
          Interactive preview
        </span>
      </div>

      <div
        className="mt-4 hidden overflow-hidden rounded-xl border border-teal-100 bg-gradient-to-br from-white to-teal-50/40 p-4 shadow-sm transition-all duration-300 sm:block"
        role="region"
        aria-live="polite"
        aria-label="Selected community post preview"
      >
        <div className="flex items-start gap-3">
          <CommunityPostAvatar
            name={focusPost.name}
            avatar={focusPost.avatar}
            ownerType={focusPost.ownerType}
            size="md"
          />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-slate-900">{focusPost.name}</p>
            <p className="text-xs text-slate-500">{focusPost.subtitle}</p>
            <p className="mt-2 text-sm font-bold leading-snug text-slate-800">{focusPost.title}</p>
            <p className="mt-1 text-xs leading-relaxed text-slate-600">{focusPost.excerpt}</p>
            <p className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-teal-700">
              <MapPin size={11} />
              {focusPost.location}
            </p>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {focusPost.tags.map(([tag, bg, color]) => (
            <span
              key={tag}
              className="rounded-full px-2 py-0.5 text-[11px] font-semibold"
              style={{ background: bg, color }}
            >
              {tag}
            </span>
          ))}
        </div>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-2 2xl:grid-cols-2 min-[1700px]:grid-cols-3">
        {LANDING_COMMUNITY_POSTS.map((post) => (
          <PostCard
            key={post.id}
            post={post}
            active={selectedId === post.id}
            onSelect={() => setSelectedId(post.id)}
            onHover={(on) => setHoveredId(on ? post.id : null)}
          />
        ))}
      </div>

      <div className="mt-6 flex flex-wrap gap-4 text-sm font-semibold text-teal-800">
        <Link href="/faq" className="transition-colors hover:text-teal-950 hover:underline">
          FAQ
        </Link>
        <Link
          href="/public-timeline"
          className="transition-colors hover:text-teal-950 hover:underline"
        >
          Public timeline
        </Link>
        <Link
          href="/about"
          className="inline-flex items-center gap-1 transition-colors hover:text-teal-950 hover:underline"
        >
          Learn more
          <ExternalLink size={13} />
        </Link>
      </div>
    </aside>
  );
}

function PostCard({
  post,
  active,
  onSelect,
  onHover,
}: {
  post: LandingCommunityPost;
  active: boolean;
  onSelect: () => void;
  onHover: (enter: boolean) => void;
}) {
  return (
    <RainbowCard active={active}>
      <article
        role="button"
        tabIndex={0}
        onClick={onSelect}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelect();
          }
        }}
        onMouseEnter={() => onHover(true)}
        onMouseLeave={() => onHover(false)}
        onFocus={() => onHover(true)}
        onBlur={() => onHover(false)}
        className={`cursor-pointer p-3.5 outline-none transition-transform duration-200 ${
          active ? 'bg-teal-50/50' : 'hover:bg-slate-50/80'
        } ${active ? 'scale-[1.01]' : 'hover:scale-[1.01]'}`}
      >
        <div className="mb-2 flex items-center gap-2.5">
          <CommunityPostAvatar name={post.name} avatar={post.avatar} ownerType={post.ownerType} />
          <div className="min-w-0">
            <p className="truncate text-sm font-bold text-slate-800">{post.name}</p>
            <p className="truncate text-[10px] font-medium text-slate-500">{post.subtitle}</p>
          </div>
        </div>
        <p className="text-sm font-bold leading-snug text-slate-900">{post.title}</p>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {post.tags.map(([tag, bg, color]) => (
            <span
              key={tag}
              className="rounded-full px-2 py-0.5 text-[11px] font-semibold"
              style={{ background: bg, color }}
            >
              {tag}
            </span>
          ))}
        </div>
      </article>
    </RainbowCard>
  );
}
